import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import NewsCard from '@/components/feature/NewsCard';
import { gossipItems, getItem, todayFeeds, channels, faqs, type ChannelKey } from '@/mocks/gossip';
import { useSeo } from '@/hooks/useSeo';
import SectionHead from './components/SectionHead';
import ChannelNav from './components/ChannelNav';
import HotRank from './components/HotRank';
import FaqBlock from './components/FaqBlock';

const heroImage =
  'https://readdy.ai/api/search-image?query=abstract%20warm%20editorial%20design%20texture%20of%20layered%20newspaper%20pages%20and%20scattered%20orange%20light%20accents%20on%20cream%20paper%2C%20soft%20duotone%20orange%20and%20ivory%2C%20artistic%20minimal%2C%20no%20text%2C%20no%20people%2C%20rich%20negative%20space%20for%20headline%20overlay&width=1600&height=900&seq=chigua-hero-2026&orientation=landscape';

function Hero() {
  const hotWords = ['顶流转型', '综艺名场面', '演唱会抢票', '古装大剧', 'AI 换脸', '社区食堂'];
  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroImage} alt="" aria-hidden="true" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 via-background-50/40 to-background-50"></div>
      </div>
      <div className="relative mx-auto flex max-w-6xl flex-col items-center px-4 py-16 text-center md:px-6 md:py-20">
        <p className="flex items-center gap-2 rounded-full border border-primary-200 bg-background-50/80 px-4 py-1.5 text-xs font-bold text-primary-700">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-60"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500"></span>
          </span>
          今日已更新 · 一口看完今天的热瓜
        </p>
        <h1 className="mt-6 font-heading text-4xl font-black leading-tight text-foreground-950 md:text-6xl">
          吃瓜日报<span className="text-primary-600">·</span>今天的瓜都在这
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-8 text-foreground-700 md:text-lg">
          每日合集，速览热门瓜料与新鲜事件。谁又翻车了、什么话题爆了、背后在吵什么——点开即看，一分钟不漏瓜。
        </p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
          <Link
            to="/daily"
            className="grid h-12 items-center rounded-full bg-primary-500 px-7 text-sm font-bold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            看今日合集
            <span className="ml-2 inline-block rounded-full bg-background-50/20 px-2 text-xs">{todayFeeds.length} 条新瓜</span>
          </Link>
          <Link
            to="/channel/yule"
            className="grid h-12 items-center rounded-full border border-background-300 bg-background-50/70 px-7 text-sm font-bold text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            逛逛娱乐瓜
          </Link>
        </div>
        <div className="mt-8 flex max-w-2xl flex-wrap items-center justify-center gap-2">
          <span className="text-xs font-bold text-foreground-500">今日热议</span>
          {hotWords.map((w) => (
            <Link
              key={w}
              to={`/daily?q=${encodeURIComponent(w)}`}
              className="rounded-full bg-background-50/70 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              # {w}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TodayPick() {
  const items = todayFeeds.map(getItem).filter((x): x is NonNullable<typeof x> => Boolean(x)).slice(0, 4);
  const main = items[0];
  const rest = items.slice(1, 4);
  if (!main) return null;
  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <SectionHead kicker="TODAY PICK" title="今日必吃合集" desc="编辑部挑出的今日重头瓜，先看这几条，跟上节奏不落空。" />
      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <Link
          to={`/news/${main.slug}`}
          className="group relative overflow-hidden rounded-xl border border-background-200 lg:col-span-2 lg:min-h-[420px]"
        >
          <img src={main.cover} alt={main.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/20 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
            <div className="flex items-center gap-2 text-xs">
              <span className="rounded-full bg-accent-500 px-2.5 py-1 font-black text-background-50">头条</span>
              <span className="rounded-full bg-background-50/20 px-2.5 py-1 text-background-100">{main.date}</span>
            </div>
            <h3 className="mt-3 font-heading text-xl font-black leading-snug text-background-50 md:text-2xl">
              {main.title}
            </h3>
            <p className="mt-2 line-clamp-2 max-w-xl text-sm leading-6 text-background-200">{main.summary}</p>
          </div>
        </Link>
        <div className="flex flex-col gap-5">
          {rest.map((it) => (
            <Link
              key={it.slug}
              to={`/news/${it.slug}`}
              className="group flex gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
            >
              <div className="relative aspect-square w-24 shrink-0 overflow-hidden rounded-lg bg-background-200">
                <img src={it.cover} alt={it.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="min-w-0 flex flex-col justify-center">
                {it.hotTag && <span className="mb-1 w-max rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-black text-accent-700">{it.hotTag}</span>}
                <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700">{it.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function LatestGrid() {
  const [tab, setTab] = useState<ChannelKey | 'all'>('all');
  const [visible, setVisible] = useState(6);
  const list = useMemo(() => {
    const sorted = [...gossipItems].sort((a, b) => (a.date < b.date ? 1 : -1));
    return tab === 'all' ? sorted : sorted.filter((i) => i.channel === tab);
  }, [tab]);
  const shown = list.slice(0, visible);
  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHead kicker="LATEST FEEDS" title="最新瓜料速递" desc="按频道追今日更新，想看哪块点哪块。" />
        <Link to="/daily" className="flex items-center gap-1 text-sm font-bold text-primary-700 hover:text-primary-800 sm:hidden">
          查看全部<i className="ri-arrow-right-line"></i>
        </Link>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setTab('all')}
          className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
            tab === 'all' ? 'bg-primary-500 font-bold text-background-50' : 'border border-background-200 bg-background-50 text-foreground-600 hover:text-primary-700'
          }`}
        >
          全部
        </button>
        {channels.map((c) => (
          <button
            key={c.key}
            onClick={() => setTab(c.key)}
            className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
              tab === c.key ? 'bg-primary-500 font-bold text-background-50' : 'border border-background-200 bg-background-50 text-foreground-600 hover:text-primary-700'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((it) => (
          <NewsCard key={it.slug} item={it} />
        ))}
      </div>
      {visible < list.length && (
        <div className="mt-8 text-center">
          <button
            onClick={() => setVisible((v) => v + 6)}
            className="inline-flex h-11 items-center gap-2 rounded-full border border-primary-200 px-6 text-sm font-bold text-primary-700 whitespace-nowrap transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            加载更多
            <i className="ri-arrow-down-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}

function TrustBand() {
  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <div className="overflow-hidden rounded-2xl bg-primary-600 text-background-50">
        <div className="grid gap-8 p-7 md:grid-cols-2 md:items-center md:p-10">
          <div>
            <p className="text-xs font-black tracking-[0.2em] text-primary-100">ABOUT US</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-3xl">
              认认真真吃瓜，本本分分整理
            </h2>
            <p className="mt-4 text-sm leading-7 text-primary-100">
              我们不追着流量跑，而是把每天纷杂的信息，整理成你能快速看懂的合集。来源尽量核对、口径尽量说明、进展尽量跟进，这是编辑部的基本盘。
            </p>
            <Link
              to="/about"
              className="mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-background-50 px-6 text-sm font-bold text-primary-700 whitespace-nowrap transition-colors hover:bg-primary-100"
            >
              关于我们<i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { n: '3', u: '个专职频道小编' },
              { n: '15+', u: '日均整理内容' },
              { n: '2023', u: '编辑部成立' },
              { n: '30w+', u: '读者累计阅读' },
            ].map((s) => (
              <div key={s.u} className="rounded-xl bg-background-50/10 p-5">
                <p className="font-heading text-3xl font-black text-background-50">{s.n}</p>
                <p className="mt-1 text-xs text-primary-100">{s.u}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  useSeo({
    title: '吃瓜日报 | 今日最新更新，每日合集速览热门瓜料',
    description:
      '吃瓜日报每日更新，汇集当日最新瓜料与热门事件。想看今日发生了什么、谁又翻车了，点开就能快速浏览每日合集，不漏掉任何一个新瓜。',
    keywords: '吃瓜日报,吃瓜日报今日,吃瓜日报每日,吃瓜日报更新,吃瓜日报最新,吃瓜日报合集',
    image: heroImage,
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '吃瓜日报',
        url: 'https://chiguadaily.example',
        slogan: '每日合集，速览今日最新瓜料与热门事件',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: '吃瓜日报',
        url: 'https://chiguadaily.example',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <Hero />

        <div className="py-12 md:py-16">
          <TodayPick />
        </div>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <LatestGrid />
        </section>

        <div className="py-12 md:py-16">
          <SectionHead kicker="CHANNELS" title="按频道找瓜" desc="各频道聚焦不同领域，追更更方便。" />
          <div className="mt-6">
            <ChannelNav />
          </div>
        </div>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 md:px-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <SectionHead kicker="HOT TOP" title="本周热议排行" desc="按阅读热度排出的本周 TOP 瓜，前三名的瓜又大又圆。" />
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                {[...gossipItems]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 4)
                  .map((it, i) => (
                    <Link key={it.slug} to={`/news/${it.slug}`} className="group flex gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
                      <span className={`font-heading text-4xl font-black leading-none ${i === 0 ? 'text-accent-600' : 'text-primary-200'}`}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700">{it.title}</h3>
                        <p className="mt-2 line-clamp-2 text-xs leading-5 text-foreground-500">{it.summary}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
            <div>
              <HotRank />
            </div>
          </div>
        </section>

        <div className="py-12 md:py-16">
          <TrustBand />
        </div>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <SectionHead kicker="FAQ" title="常见问题" desc="关于更新节奏、信息来源与转载授权，这里都有答案。" />
            <div className="mt-7">
              <FaqBlock />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}